"use client"

import { useCallback, useState } from "react"
import Button, { type ButtonSize } from "@atoms/Button"
import ContactModal from "@molecules/ContactModal"
import { Icon } from "@/shared/ui/icon"
import { EARLY_ACCESS_LABEL } from "@lib/early-access"
import { pushGtmEvent, type CtaPlacement } from "@lib/gtm"
import { cn } from "@/lib/utils"

export type EarlyAccessButtonProps = {
  /** Место на сайте: уходит в amoCRM и Telegram как источник лида. */
  source: string
  placement: CtaPlacement
  /** secondary — когда рядом уже стоит другое первичное действие (One Signal Rule). */
  variant?: "primary" | "secondary"
  size?: ButtonSize
  /** На странице Atlas дублирует прежние события воронки, чтобы теги GTM их не потеряли. */
  atlasEvents?: boolean
  className?: string
}

/**
 * Единственная кнопка конверсии на сайте: одна подпись, одна форма.
 * Отличается только источник лида, по которому видно, откуда пришёл человек.
 */
const EarlyAccessButton = ({
  source,
  placement,
  variant = "primary",
  size = "lg",
  atlasEvents = false,
  className = "",
}: EarlyAccessButtonProps) => {
  const [leadSource, setLeadSource] = useState<string | null>(null)

  const open = () => {
    const page = window.location.href
    // Метка кампании из ?source= важнее места на странице, как у прежних форм.
    const resolved =
      new URLSearchParams(window.location.search).get("source") ||
      `early-access:${source}`

    setLeadSource(resolved)
    pushGtmEvent("early_access_open", { page, source: resolved, placement })

    if (atlasEvents) {
      const href = "#early-access"
      pushGtmEvent("atlas_cta_click", {
        page,
        placement,
        label: EARLY_ACCESS_LABEL,
        href,
      })
      pushGtmEvent("atlas_registration_flow_start", { page, placement, href })
    }
  }

  const close = useCallback(() => setLeadSource(null), [])

  const isPrimary = variant === "primary"

  return (
    <>
      <Button
        type="button"
        aria-haspopup="dialog"
        variant={isPrimary ? "gradientPill" : "secondary"}
        size={size}
        trailingIcon={
          isPrimary ? (
            <Icon name="arrow-right-linear" aria-hidden="true" className="size-5" />
          ) : undefined
        }
        onClick={open}
        className={cn(
          "motion-safe:active:scale-[0.98]",
          // На узком экране подписи с кружком тесно в полном отступе lg.
          isPrimary && size === "lg" && "max-sm:pl-5",
          !isPrimary &&
            "dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/15",
          className,
        )}
      >
        {EARLY_ACCESS_LABEL}
      </Button>

      <ContactModal
        isOpen={leadSource !== null}
        onClose={close}
        source={leadSource ?? undefined}
      />
    </>
  )
}

export default EarlyAccessButton
